/**
 * The prompt chips Aria offers in her empty state.
 *
 * Split out of ChatWidget so the selection rule is a pure function with no React
 * around it — it is the part with actual logic, and it is what
 * scripts/check_aria_suggestions.py audits.
 *
 * Two things decide whether a chip is offered:
 *
 *   role         — can this person act on it at all
 *   entitlement  — did this workspace buy the module the prompt reaches into
 *
 * The entitlement half is the bug this file exists to fix. The chips used to be a
 * flat role -> string[] map with no module dimension, so a workspace that never
 * bought Recruitment was still offered "What open job positions do we have?" — to
 * every role, including employees — and Aria answered "that isn't part of your
 * plan". She was advertising four prompts she was then obliged to refuse.
 *
 * This is the display-side counterpart of _unowned_actions in routers/chat.py,
 * which withholds *tools* for unowned modules for the same reason: never offer
 * something you would have to refuse.
 *
 * Spec: .claude/specs/aria-module-hint-and-suggestions.md
 */
import { ModuleKey, ModuleMap, ownsModule } from "./moduleMetadata";

/** How many chips the empty state renders. */
export const CHIP_COUNT = 4;

export interface AriaSuggestion {
  text: string;
  /**
   * The module whose data the prompt reaches into, or null for Core.
   *
   * Core is the important half of this map, and the non-obvious one. Expense
   * *claims* are Core — manual reimbursement is free to everyone, and only the AI
   * capture of receipts is the paid module. Attendance, timesheets, leave and a
   * user's own payslip are Core too. Getting one of these wrong the pessimistic way
   * hides a chip from tenants entitled to it, which no test can see; check the
   * context builders in routers/chat.py before tagging a new chip.
   */
  module: ModuleKey | null;
  /** Roles offered this prompt. */
  roles: readonly string[];
}

/** Roles with their own chip set. Anything else — contractor, admin_assistant —
 *  falls back to the employee set, which is what the old `default` key did. */
export const KNOWN_ROLES = ["admin", "hr", "recruiter", "finance", "manager", "employee"] as const;

/**
 * The catalogue, in the order chips are offered.
 *
 * Ordered operational-before-personal, so the first four a role sees are the ones
 * they can act on. Every entry is answerable from that role's context builder in
 * routers/chat.py — a chip for data Aria cannot see is the same broken promise as
 * a chip for a module the tenant does not own. eSign and AI Expense Capture have
 * no chips because no context builder exposes their data yet; on those pages the
 * role's general chips show instead.
 *
 * Roles are spelled out per entry rather than pulled from shared constants, so
 * scripts/check_aria_suggestions.py can read this as data with a plain regex. It
 * is a census of a map, and a census that has to evaluate the file it audits is
 * one refactor away from silently passing.
 */
export const SUGGESTION_CATALOGUE: readonly AriaSuggestion[] = [
  { module: null, text: "Show me pending leave requests",
    roles: ["admin", "hr", "manager"] },
  { module: null, text: "Show pending expense claims",
    roles: ["admin", "hr", "manager", "finance"] },

  // Not offered to employees, though the old default list did offer it. The
  // personal context carries leave, attendance, timesheets, payslips, assets and
  // policies — no vacancies — so for an employee this chip produced "I don't have
  // that in my records" whether or not the tenant owned Recruitment. Entitlement
  // was only ever half of why a chip could be a dead end. Worth revisiting if
  // internal openings ever reach the personal context; until then, don't offer it.
  { module: "recruitment", text: "What open job positions do we have?",
    roles: ["admin", "hr", "recruiter"] },
  { module: "recruitment", text: "Help me create a new job posting",
    roles: ["admin", "hr", "recruiter"] },
  { module: "recruitment", text: "How many candidates applied this week?",
    roles: ["admin", "hr", "recruiter"] },

  { module: "finance", text: "What invoices are overdue?",
    roles: ["admin", "finance"] },
  { module: "finance", text: "Show total outstanding amount",
    roles: ["admin", "finance"] },

  // Only the finance context carries the payroll block, and the admin context
  // composes it. HR never sees payroll totals, so HR is not offered the question.
  { module: "payroll", text: "What's this month's total payroll?",
    roles: ["admin", "finance"] },

  { module: "performance", text: "Which review cycles are still open?",
    roles: ["admin", "hr"] },

  { module: null, text: "Show my team's attendance today",
    roles: ["manager"] },

  // The tail is Core and open to everyone, which is what makes the count hold up
  // for a tenant that owns no modules at all: every role reaches CHIP_COUNT on
  // these four alone. check_aria_suggestions.py asserts exactly that.
  { module: null, text: "Who's on leave this week?",
    roles: ["admin", "hr", "recruiter", "finance", "manager", "employee"] },
  { module: null, text: "What's my leave balance?",
    roles: ["admin", "hr", "recruiter", "finance", "manager", "employee"] },
  { module: null, text: "What are the upcoming public holidays?",
    roles: ["admin", "hr", "recruiter", "finance", "manager", "employee"] },
  { module: null, text: "How many hours did I log this week?",
    roles: ["admin", "hr", "recruiter", "finance", "manager", "employee"] },
];

/** The chip set a role uses — unknown roles get the employee set. */
export function normalizeRole(role: string): string {
  return (KNOWN_ROLES as readonly string[]).includes(role) ? role : "employee";
}

/**
 * The chips to render, in order.
 *
 * `activeModule` promotes that module's chips to the front; it never adds one the
 * role would not otherwise be offered. An employee on a Payroll page is not shown
 * payroll-administration prompts — they get their normal chips, because the page
 * you are standing on does not change what you are allowed to do.
 *
 * While `modules` is undefined (the entitlement query has not resolved) every
 * module chip reads as unowned, so the first paint is Core-only and module chips
 * appear as the answer arrives. That matches useModules' own stance: appearing
 * late beats flashing on screen and being pulled away.
 */
export function selectSuggestions(
  role: string,
  modules: ModuleMap | undefined,
  activeModule: ModuleKey | null,
  limit: number = CHIP_COUNT,
): AriaSuggestion[] {
  const normalized = normalizeRole(role);

  const eligible = SUGGESTION_CATALOGUE.filter(
    s => s.roles.includes(normalized) && (s.module === null || ownsModule(modules, s.module)),
  );

  // Stable partition: active-module chips first, catalogue order preserved inside
  // each half. A sort() comparator would have been enough in every browser that
  // implements a stable sort, but this states the intent instead of relying on it.
  const active = activeModule && ownsModule(modules, activeModule)
    ? eligible.filter(s => s.module === activeModule)
    : [];
  const rest = eligible.filter(s => !active.includes(s));

  return [...active, ...rest].slice(0, limit);
}
